// frontend/src/pages/PaychequeList/useMutations.ts
// Async mutations for the paycheque list.
// Hook owns the API calls, validation, and loading flags.
// Parent owns side effects (updating local state, closing modals, alerting the user).

import { useState } from "react";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";

interface SaveChequeNumberResult {
  success: boolean;
  chequeNumber?: string | null;
  error?: string;
}

interface VoidPaychequeResult {
  success: boolean;
  error?: string;
}

export function usePaychequeMutations() {
  const [savingChq, setSavingChq] = useState<boolean>(false);
  const [voiding, setVoiding] = useState<boolean>(false);

  /**
   * Save a cheque number for a paycheque.
   * Returns a result object; parent is responsible for showing errors and updating UI.
   */
  async function saveChequeNumber(
    stubId: string,
    value: string
  ): Promise<SaveChequeNumberResult> {
    const trimmed = String(value || "").trim();

    // Validation (empty = clear the cheque number; otherwise must look like a cheque number)
    if (trimmed) {
      if (!/^\d+$/.test(trimmed)) {
        return { success: false, error: "Cheque number must contain only digits (0-9). No letters, spaces, or dashes." };
      }
      if (trimmed.length < 3) {
        return { success: false, error: "Cheque number must be at least 3 digits." };
      }
      if (trimmed.length > 10) {
        return { success: false, error: "Cheque number cannot be more than 10 digits." };
      }
      if (parseInt(trimmed, 10) === 0) {
        return { success: false, error: "Cheque number cannot be zero." };
      }
    }

    setSavingChq(true);
    try {
      const res = await apiFetch(
        "/api/v1/payroll/paycheques/" + stubId + "/cheque-number",
        {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({ cheque_number: trimmed || null }),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({} as any));
        return { success: false, error: errData.detail || "Save failed" };
      }
      const data = await res.json();
      return { success: true, chequeNumber: data.cheque_number };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    } finally {
      setSavingChq(false);
    }
  }

  /**
   * Void a paycheque with a reason. Returns a result object.
   */
  async function voidPaycheque(
    stubId: string,
    reason: string
  ): Promise<VoidPaychequeResult> {
    setVoiding(true);
    try {
      const res = await apiFetch(
        "/api/v1/payroll/paycheques/" + stubId + "/void",
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ reason: reason.trim() }),
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        return { success: false, error: txt || "Void failed" };
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    } finally {
      setVoiding(false);
    }
  }

  return {
    saveChequeNumber,
    voidPaycheque,
    savingChq,
    voiding,
  };
}